import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';

export interface ServerError {
  id: string;
  timestamp: number;
  category: string;
  message: string;
  details?: Record<string, unknown>;
  severity: 'info' | 'warning' | 'error' | 'critical';
  socketId?: string;
  characterId?: string;
}

interface ServerFeedbackPanelProps {
  socket: Socket | null;
  isEnabled: boolean;
}

const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return '#ff3333';
    case 'error':
      return '#ff6633';
    case 'warning':
      return '#ffaa33';
    case 'info':
      return '#33aaff';
    default:
      return '#888888';
  }
};

const getSeverityBg = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return '#330000';
    case 'error':
      return '#331100';
    case 'warning':
      return '#333300';
    case 'info':
      return '#003333';
    default:
      return '#1a1a1a';
  }
};

export const ServerFeedbackPanel: React.FC<ServerFeedbackPanelProps> = ({ socket, isEnabled }) => {
  const [errors, setErrors] = useState<ServerError[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!socket || !isEnabled) return;

    // Load history on connect
    const handleHistory = (history: ServerError[]): void => {
      setErrors(history);
    };

    // Listen for new errors
    const handleFeedback = (error: ServerError): void => {
      setErrors(prev => {
        const updated = [...prev, error];
        // Keep only last 50 errors
        if (updated.length > 50) {
          updated.shift();
        }
        return updated;
      });
    };

    socket.on('server:feedback:history', handleHistory);
    socket.on('server:feedback', handleFeedback);

    return () => {
      socket.off('server:feedback:history', handleHistory);
      socket.off('server:feedback', handleFeedback);
    };
  }, [socket, isEnabled]);

  const clearErrors = useCallback((): void => {
    setErrors([]);
  }, []);

  const removeError = useCallback((id: string): void => {
    setErrors(prev => prev.filter(e => e.id !== id));
  }, []);

  if (!isEnabled || errors.length === 0) {
    return null;
  }

  const criticalCount = errors.filter(e => e.severity === 'critical').length;
  const errorCount = errors.filter(e => e.severity === 'error').length;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        maxWidth: 400,
        maxHeight: isExpanded ? 600 : 120,
        backgroundColor: '#0d1117',
        border: `1px solid ${criticalCount > 0 ? '#ff3333' : errorCount > 0 ? '#ff6633' : '#30363d'}`,
        borderRadius: 0,
        fontFamily: "'Sometype Mono', monospace",
        fontSize: 11,
        color: '#8b949e',
        zIndex: 5000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 12px',
          backgroundColor: '#161b22',
          borderBottom: '1px solid #30363d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold', color: '#e6edf3' }}>
            SERVER FEEDBACK
          </span>
          {criticalCount > 0 && (
            <span style={{ color: '#ff3333', fontWeight: 'bold' }}>
              [{criticalCount} CRITICAL]
            </span>
          )}
          {errorCount > 0 && (
            <span style={{ color: '#ff6633', fontWeight: 'bold' }}>
              [{errorCount} ERROR]
            </span>
          )}
        </div>
        <span style={{ color: '#6e7681' }}>{isExpanded ? '▼' : '▶'}</span>
      </div>

      {/* Error List */}
      {isExpanded && (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {errors.map(error => (
            <div
              key={error.id}
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid #21262d',
                backgroundColor: getSeverityBg(error.severity),
                borderLeft: `3px solid ${getSeverityColor(error.severity)}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: getSeverityColor(error.severity), fontWeight: 'bold', fontSize: 10 }}>
                  [{error.category.toUpperCase()}]
                </div>
                <div style={{ color: '#a0aec0', fontSize: 10, marginTop: 2, wordBreak: 'break-word' }}>
                  {error.message}
                </div>
                {error.details && (
                  <div style={{ color: '#6e7681', fontSize: 9, marginTop: 4, fontStyle: 'italic' }}>
                    {JSON.stringify(error.details).substring(0, 100)}
                    {JSON.stringify(error.details).length > 100 ? '...' : ''}
                  </div>
                )}
                <div style={{ color: '#6e7681', fontSize: 9, marginTop: 2 }}>
                  {new Date(error.timestamp).toLocaleTimeString()}
                </div>
              </div>
              <button
                onClick={() => removeError(error.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6e7681',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 12,
                  flexShrink: 0,
                }}
                title="Remove error"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      {isExpanded && (
        <div
          style={{
            padding: '6px 12px',
            borderTop: '1px solid #30363d',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={clearErrors}
            style={{
              background: 'none',
              border: '1px solid #30363d',
              color: '#8b949e',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: 10,
              fontFamily: "'Sometype Mono', monospace",
            }}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};
